<template>
  <div class="h-screen flex flex-col">
    <!-- Header with fixed height -->
    <div class="bg-crypto-dark">
    </div>

    <!-- Scrollable content below -->
    <div class="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800 bg-crypto-bg">
      <router-view :key="$route.fullPath" />
    </div>
  </div>
</template>

<script setup>
import { watch, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useHead } from '@vueuse/head'
import { buildStructuredData } from '@/utils/structuredData'
import { countryData } from '@/data/companyRegistration'

const route = useRoute()

// ---- SEO / head management -------------------------------------------------
const SITE_NAME = 'Somani Global'
const BASE_URL = 'https://fintech.somaniglobal.com'
const DEFAULT_TITLE =
  'Crypto & Gaming License Consulting + Company Formation | Somani Global'
const DEFAULT_DESCRIPTION =
  'Somani Global helps you obtain crypto licenses, gaming licenses and register companies across 20+ jurisdictions, with expert end-to-end compliance support.'

// Dynamic company-registration pages derive their meta from the country data
// (the route itself has no static meta).
const companyRegEntry = computed(() => {
  if (route.path.startsWith('/services/company-registration/')) {
    return countryData[route.path.split('/').pop()] || null
  }
  return null
})

const title = computed(() => {
  if (companyRegEntry.value)
    return `Company Registration in ${companyRegEntry.value.name} | Somani Global`
  return route.meta.title || DEFAULT_TITLE
})
const description = computed(() => {
  if (companyRegEntry.value)
    return `Register a company in ${companyRegEntry.value.name} with Somani Global — structure advice, documentation, banking and compliance, end-to-end.`
  return route.meta.description || DEFAULT_DESCRIPTION
})
const canonical = computed(() => BASE_URL + route.path)
const robots = computed(() =>
  route.meta.noindex ? 'noindex, nofollow' : 'index, follow'
)
const structuredData = computed(() =>
  JSON.stringify(buildStructuredData(route))
)

useHead({
  title,
  meta: [
    { name: 'description', content: description },
    { name: 'robots', content: robots },
    { property: 'og:title', content: title },
    { property: 'og:description', content: description },
    { property: 'og:type', content: 'website' },
    { property: 'og:site_name', content: SITE_NAME },
    { property: 'og:url', content: canonical },
    { property: 'og:image', content: `${BASE_URL}/assets/logos/logo.png` },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: title },
    { name: 'twitter:description', content: description },
    { name: 'twitter:image', content: `${BASE_URL}/assets/logos/logo.png` },
  ],
  link: [{ rel: 'canonical', href: canonical }],
  script: [{ type: 'application/ld+json', children: structuredData }],
})

// ---- Reset scroll position on route change ---------------------------------
watch(
  () => route.fullPath,
  () => {
    const scrollContainer = document.querySelector('.flex-1')
    if (scrollContainer) {
      scrollContainer.scrollTo({ top: 0, behavior: 'auto' }) // 🔁 No smooth scroll
    }
  }
)
</script>


<style>
html {
  scroll-behavior: smooth;
}
</style>
